import posthog from 'posthog-js'

import { resolvePostHogRuntimeOptions } from './posthog.runtime'

export type UsageEventName = 'screen_viewed' | 'feature_used' | 'auth_flow'
export type UsageEventSurface =
  | 'route'
  | 'session_auth'
  | 'gallery_toolbar'
  | 'grid-context-menu'
  | 'grid-actions-menu'
  | 'alerts'
  | 'inventory_import'
  | 'orders'
  | 'onboarding'
  | 'onboarding_resource_start'
  | 'places_import'
  | 'project_details'
  | 'project_reporting'
  | 'projects_overview'
  | 'review_workflow'
  | 'settings_tracking_tags'
  | 'shop_connector_settings'
  | 'shop_payment_settings'
  | 'shop_products'
  | 'shop_profile_settings'
  | 'shop_publication_settings'
  | 'shop_seo_settings'
  | 'references_options_lists'
  | 'references_properties'
export type UsageEventValue = string | number | boolean
export type UsageEventKey =
  | 'screen_key'
  | 'feature'
  | 'action'
  | 'surface'
  | 'result'
  | 'scope_kind'
  | 'is_guest'
  | 'flag_key'
  | 'reason'
  | 'status_type'
  | 'status_action'
  | 'export_kind'
  | 'export_template'
  | 'qr_count'
  | 'row_count'
  | 'product_count'
  | 'picture_count'
  | 'created_count'
  | 'updated_count'
  | 'failed_count'
  | 'skipped_count'
  | 'reviewed_count'
  | 'error_count'
  | 'field_key'
  | 'platform'
  | 'order_type'
  | 'setting_enabled'
  | 'has_media'
  | 'auth_cookie_count'
  | 'auth_cookie_bytes'
  | 'auth_cookie_largest_bytes'
  | 'auth_local_storage_count'
  | 'auth_session_storage_count'
  | 'auth_cookie_readable'
  | 'auth_local_storage_readable'
  | 'auth_session_storage_readable'
  | 'cookie_enabled'
  | 'secure_context'
  | 'onboarding_id'
  | 'onboarding_version'
  | 'onboarding_flow'
  | 'step_id'
  | 'objective'
  | 'start_mode'
  | 'release_stage'
  | 'error_code'

type ScreenViewedProps = {
  screen_key: string
  surface?: Extract<UsageEventSurface, 'route'>
}

type FeatureUsedProps = {
  feature: string
  action: string
  surface: Exclude<UsageEventSurface, 'route' | 'session_auth'>
  result?: string
  scope_kind?: string
  is_guest?: boolean
  flag_key?: string
  status_type?: string
  status_action?: string
  export_kind?: string
  export_template?: string
  qr_count?: number
  row_count?: number
  product_count?: number
  picture_count?: number
  created_count?: number
  updated_count?: number
  failed_count?: number
  skipped_count?: number
  reviewed_count?: number
  error_count?: number
  field_key?: string
  platform?: string
  order_type?: string
  setting_enabled?: boolean
  has_media?: boolean
  onboarding_id?: string
  onboarding_version?: number
  onboarding_flow?: 'new_account' | 'team_invitation'
  step_id?: string
  objective?: string
  start_mode?: string
  release_stage?: string
  error_code?: string
}

type AuthFlowProps = {
  action: string
  surface?: Extract<UsageEventSurface, 'session_auth'>
  result?: string
  reason?: string
  auth_cookie_count?: number
  auth_cookie_bytes?: number
  auth_cookie_largest_bytes?: number
  auth_local_storage_count?: number
  auth_session_storage_count?: number
  auth_cookie_readable?: boolean
  auth_local_storage_readable?: boolean
  auth_session_storage_readable?: boolean
  cookie_enabled?: boolean
  secure_context?: boolean
}

type UsageEventPropsByName = {
  screen_viewed: ScreenViewedProps
  feature_used: FeatureUsedProps
  auth_flow: AuthFlowProps
}

export type UsageEventProps<T extends UsageEventName = UsageEventName> =
  UsageEventPropsByName[T]

const ALLOWED_USAGE_EVENT_KEYS = new Set<UsageEventKey>([
  'screen_key',
  'feature',
  'action',
  'surface',
  'result',
  'scope_kind',
  'is_guest',
  'flag_key',
  'reason',
  'status_type',
  'status_action',
  'export_kind',
  'export_template',
  'qr_count',
  'row_count',
  'product_count',
  'picture_count',
  'created_count',
  'updated_count',
  'failed_count',
  'skipped_count',
  'reviewed_count',
  'error_count',
  'field_key',
  'platform',
  'order_type',
  'setting_enabled',
  'has_media',
  'auth_cookie_count',
  'auth_cookie_bytes',
  'auth_cookie_largest_bytes',
  'auth_local_storage_count',
  'auth_session_storage_count',
  'auth_cookie_readable',
  'auth_local_storage_readable',
  'auth_session_storage_readable',
  'cookie_enabled',
  'secure_context',
  'onboarding_id',
  'onboarding_version',
  'onboarding_flow',
  'step_id',
  'objective',
  'start_mode',
  'release_stage',
  'error_code',
])

export function sanitizeUsageEventProps(
  props: Record<string, unknown>,
): Record<string, UsageEventValue> {
  const sanitizedEntries = Object.entries(props).flatMap(([key, value]) => {
    if (!ALLOWED_USAGE_EVENT_KEYS.has(key as UsageEventKey)) return []
    if (
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean'
    ) {
      return []
    }
    if (typeof value === 'number' && !Number.isFinite(value)) return []
    if (typeof value === 'string' && value.length === 0) return []

    return [[key, value] as const]
  })

  return Object.fromEntries(sanitizedEntries)
}

export function trackUsageEvent<T extends UsageEventName>(
  event: T,
  props: UsageEventProps<T>,
): void {
  try {
    if (!resolvePostHogRuntimeOptions().analyticsEnabled) return

    posthog.capture(event, sanitizeUsageEventProps(props))
  } catch (error) {
    console.error(error)
  }
}
